import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import dayjs from 'shared/helpers/dayjs';
import useWindowSize from 'shared/hooks/useWindowResize';
import breakpoints from 'theme/atoms/breakpoints';
import colours from 'theme/atoms/colours';

import * as RechartsStyled from 'dashboard/styles/Recharts.style';
import * as Styled from 'dashboard/styles/Overview.style';
import * as Text from 'theme/components/Text.style';
import { useUrgencyStatistics } from 'dashboard/hooks/useUrgencyStatistics';
import aggregateByWeek from 'dashboard/helpers/aggregateByWeek';
import aggregateByMonth from 'dashboard/helpers/aggregateByMonth';
import { isTouchDevice } from 'shared/helpers/device';
import BarShape from './BarShape';
import CursorShape from './CursorShape';
import CustomTooltip from './CustomTooltip';
import CustomLegend from './CustomLegend';
import EmptyState from './EmptyState';

const WEEKLY_THRESHOLD_DAYS = 60;
const MONTHLY_THRESHOLD_DAYS = 365;
const TICK_LABEL_WIDTH = 65;

export type DateGrouping = 'daily' | 'weekly' | 'monthly';

interface UrgencySectionProps {
  queryParams: string;
}

export default function UrgencySection({ queryParams }: UrgencySectionProps) {
  const { t, i18n } = useTranslation('pageDashboard');
  const { data: rawData, isSuccess } = useUrgencyStatistics(queryParams);
  const [windowWidth] = useWindowSize();

  const dateFormat = i18n.language === 'fr' ? 'D MMM' : 'MMM D';
  const isDesktop = windowWidth > parseInt(breakpoints.tablet, 10);

  const grouping: DateGrouping = useMemo(() => {
    if (!rawData?.length || rawData.length < 2) return 'daily';
    const rangeInDays = dayjs(rawData[rawData.length - 1].date).diff(
      dayjs(rawData[0].date),
      'day'
    );
    if (rangeInDays > MONTHLY_THRESHOLD_DAYS) return 'monthly';
    if (rangeInDays > WEEKLY_THRESHOLD_DAYS) return 'weekly';
    return 'daily';
  }, [rawData]);

  const data = useMemo(() => {
    if (!rawData) return rawData;
    if (grouping === 'monthly') return aggregateByMonth(rawData);
    if (grouping === 'weekly') return aggregateByWeek(rawData);
    return rawData;
  }, [rawData, grouping]);

  const isEmpty =
    !data?.length ||
    data.every(
      (d) => d.emergency + d.needs_attention + d.routine + d.informational === 0
    );

  const [chartWidth, setChartWidth] = useState(0);

  const chartWrapperRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return undefined;
    setChartWidth(node.clientWidth);
    const observer = new ResizeObserver((entries) => {
      setChartWidth(entries[0].contentRect.width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const tickInterval = useMemo(() => {
    if (!data?.length || !chartWidth) return 0;
    const maxLabels = Math.floor(chartWidth / TICK_LABEL_WIDTH);
    if (data.length <= maxLabels) return 0;
    return Math.ceil(data.length / maxLabels) - 1;
  }, [data, chartWidth]);

  const bars = [
    {
      dataKey: 'informational',
      name: t('urgency.informational'),
      fill: colours.neutral[200],
    },
    { dataKey: 'routine', name: t('urgency.routine'), fill: colours.teal[500] },
    {
      dataKey: 'needs_attention',
      name: t('urgency.needsAttention'),
      fill: colours.warning[500],
    },
    {
      dataKey: 'emergency',
      name: t('urgency.emergency'),
      fill: colours.primary[500],
    },
  ];

  const renderChart = () => (
    <ResponsiveContainer width="100%" height={420}>
      <BarChart
        data={data}
        barCategoryGap="4px"
        margin={{ top: 8, right: 5 }}
        style={{
          fontFamily: 'Inter, sans-serif',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        }}
      >
        <XAxis
          dataKey="date"
          tickFormatter={(value) =>
            dayjs(value).format(grouping === 'monthly' ? 'MMM' : dateFormat)
          }
          interval={tickInterval}
          tickLine={false}
          stroke={colours.neutral[200]}
          tick={{
            fill: colours.neutral[500],
            fontSize: 13,
            fontWeight: 650,
            dy: 10,
          }}
          padding={{ left: 15 }}
        />
        <YAxis
          allowDecimals={false}
          axisLine={false}
          tickLine={false}
          tick={{
            fill: colours.neutral[500],
            fontSize: 13,
            fontWeight: 650,
          }}
          width={isDesktop ? undefined : 30}
          label={
            isDesktop
              ? {
                  value: t('urgency.numberOfSignals'),
                  angle: -90,
                  position: 'insideLeft',
                  dx: 5,
                  style: {
                    textAnchor: 'middle',
                    fill: colours.neutral[900],
                    fontSize: 13,
                    fontWeight: 650,
                    fontFamily: 'Inter, sans-serif',
                  },
                }
              : undefined
          }
        />
        <CartesianGrid stroke={colours.neutral[300]} vertical={false} />
        <Tooltip
          trigger={isTouchDevice() ? 'click' : 'hover'}
          content={
            <CustomTooltip
              dateFormat={'LL'}
              notesLabel={(count) => t('urgency.signals', { count })}
              grouping={grouping}
            />
          }
          cursor={<CursorShape />}
        />
        {bars.map((bar) => (
          <Bar
            key={bar.dataKey}
            dataKey={bar.dataKey}
            name={bar.name}
            stackId="a"
            fill={bar.fill}
            shape={<BarShape />}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );

  if (!isSuccess) return null;

  return (
    <Styled.SectionContainer>
      <Text.H5>{t('urgency.signalVolumeAndUrgency')}</Text.H5>

      <Styled.Card>
        <RechartsStyled.ChartContainer>
          <CustomLegend
            payload={[...bars].reverse().map((bar) => ({
              value: bar.name,
              color: bar.fill,
              type: 'rect' as const,
            }))}
          />
          {isEmpty ? (
            <EmptyState isDesktop={isDesktop} />
          ) : (
            <RechartsStyled.ChartArea ref={chartWrapperRef}>
              {renderChart()}
            </RechartsStyled.ChartArea>
          )}
        </RechartsStyled.ChartContainer>
      </Styled.Card>
    </Styled.SectionContainer>
  );
}
